/**
 * Cart Drawer Component
 * Handles the cart drawer functionality including open/close and slide animations
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.overlay = null;
    this.closeButton = null;
    this.cartTrigger = null;
    this.content = null;
    this.isOpen = false;
    this.isRefreshing = false;
    this.isAddingToCart = false;

    // Store bound event handlers for proper cleanup
    this.boundQuantityHandlers = new WeakMap();
    this.boundRemoveHandlers = new WeakMap();

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleCartTriggerClick = this.handleCartTriggerClick.bind(this);
    this.refreshCart = this.refreshCart.bind(this);
  }

  connectedCallback() {
    this.init();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  init() {
    // Get drawer elements
    this.drawer = document.getElementById('CartDrawer');
    if (!this.drawer) return;

    this.overlay = this.drawer.querySelector('.cart-drawer__overlay');
    this.closeButton = this.drawer.querySelector('#CartDrawerClose');
    this.content = this.drawer.querySelector('.cart-drawer__content');

    // Get cart trigger element
    this.cartTrigger = document.querySelector('.header__cart');

    // Ensure drawer is initially closed
    this.drawer.classList.remove('cart-drawer--open');
    this.isOpen = false;

    // Bind cart content events
    this.bindCartContentEvents();

    // Initialize additional products functionality
    this.initAdditionalProducts();

    // Initialize cart item count
    this.updateCartItemCount();

    if (window.location.search.includes('cart-drawer=open')) {
      this.open();
    }
  }

  bindEvents() {
    if (!this.drawer) return;

    // Bind cart trigger click
    if (this.cartTrigger) {
      this.cartTrigger.addEventListener('click', this.handleCartTriggerClick);
    }

    // Bind close button click
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }

    // Bind overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Bind escape key
    document.addEventListener('keydown', this.handleEscapeKey);

    // Bind cart/add form submissions
    this.handleAddToCartFormSubmit = this.handleAddToCartFormSubmit.bind(this);
    document.addEventListener('submit', this.handleAddToCartFormSubmit);
  }

  unbindEvents() {
    if (this.cartTrigger) {
      this.cartTrigger.removeEventListener('click', this.handleCartTriggerClick);
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.handleOverlayClick);
    }

    document.removeEventListener('keydown', this.handleEscapeKey);
    document.removeEventListener('submit', this.handleAddToCartFormSubmit);
  }

  handleCartTriggerClick(event) {
    event.preventDefault();
    this.open();
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  async handleAddToCartFormSubmit(event) {
    const form = event.target;

    // Only handle forms with action containing "/cart/add"
    const formAction = form.getAttribute('action') || '';
    if (!formAction.startsWith('/cart/add')) {
      return;
    }

    event.preventDefault();

    // Prevent double submissions
    if (this.isAddingToCart) {
      return;
    }

    this.isAddingToCart = true;

    // Get and disable submit button to prevent multiple clicks
    const submitButton = form.querySelector('[type="submit"]');
    const originalButtonText = submitButton?.textContent;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding...';
    }

    // Extract form data
    const formData = new FormData(form);
    const items = [];

    // Get variant ID and quantity (keep as string for large IDs)
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);

    if (!variantId) {
      console.error('No variant ID found in form');
      this.isAddingToCart = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
      return;
    }

    // Build items array for Shopify Ajax Cart API (use string ID for safety with large IDs)
    items.push({
      id: variantId,
      quantity: quantity
    });

    // Add any properties if they exist
    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propertyKey = key.match(/properties\[(.+)\]/)?.[1];
        if (propertyKey) {
          properties[propertyKey] = value;
        }
      }
    }

    // Add properties to the item if any exist
    if (Object.keys(properties).length > 0) {
      items[0].properties = properties;
    }

    const requestData = { items };

    try {
      // Submit via Shopify Ajax Cart API
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.description || result.message || 'Failed to add to cart';
        throw new Error(errorMessage);
      }

      // Dispatch success event
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { items: result.items || result, form }
      }));

      // Refresh cart content first, then open drawer (prevents stale content flash)
      await this.refreshCart();
      this.open();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(error.message);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('cart:add-error', {
        detail: { error, form }
      }));
    } finally {
      // Re-enable form submission
      this.isAddingToCart = false;
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    }
  }

  open() {
    if (!this.drawer || this.isOpen) return;

    this.drawer.classList.add('cart-drawer--open');
    this.isOpen = true;

    // Prevent body scroll when drawer is open
    document.body.style.overflow = 'hidden';

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('cart-drawer:open', {
      bubbles: true,
      detail: { drawer: this.drawer }
    }));

    // Focus management for accessibility
    this.#trapFocus();
  }

  close() {
    if (!this.drawer || !this.isOpen) return;

    this.drawer.classList.remove('cart-drawer--open');
    this.isOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('cart-drawer:close', {
      bubbles: true,
      detail: { drawer: this.drawer }
    }));

    // Return focus to cart trigger if it exists
    if (this.cartTrigger) {
      this.cartTrigger.focus();
    }
  }

  async refreshCart() {
    if (!this.content || this.isRefreshing) return;

    this.isRefreshing = true;

    // Add loading state with overlay
    this.content.classList.add('cart-drawer__content--loading');

    try {
      // Fetch updated cart HTML using Shopify's section rendering API
      const response = await fetch('/cart?section_id=cart-drawer');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Create a temporary container to parse the response
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = html;

      // Extract the cart content from the response
      const newContent = tempContainer.querySelector('.cart-drawer__content');

      if (newContent) {
        // Clean up existing event listeners before replacing content
        this.unbindCartContentEvents();

        // Replace the current content with new content
        this.content.innerHTML = newContent.innerHTML;

        // Update references to elements that might have changed
        this.closeButton = this.drawer.querySelector('#CartDrawerClose');

        // Bind close button event listener
        if (this.closeButton) {
          this.closeButton.addEventListener('click', this.handleCloseClick);
        }

        // Re-bind cart content events
        this.bindCartContentEvents();

        // Initialize additional products functionality
        this.initAdditionalProducts();

        // Update cart item count
        this.updateCartItemCount();

        // Dispatch refresh event
        document.dispatchEvent(new CustomEvent('cart:refreshed', {
          detail: { drawer: this.drawer }
        }));
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('cart:refresh-error', {
        detail: { error }
      }));
    } finally {
      // Remove loading state
      this.content.classList.remove('cart-drawer__content--loading');
      this.isRefreshing = false;
    }
  }

  unbindCartContentEvents() {
    if (!this.content) return;

    // Remove quantity button listeners
    const quantityButtons = this.content.querySelectorAll('.cart-drawer__quantity-remove, .cart-drawer__quantity-add');
    quantityButtons.forEach(button => {
      const handler = this.boundQuantityHandlers.get(button);
      if (handler) {
        button.removeEventListener('click', handler);
        this.boundQuantityHandlers.delete(button);
      }
    });

    // Remove item remove button listeners
    const removeButtons = this.content.querySelectorAll('.cart-drawer__item-remove');
    removeButtons.forEach(button => {
      const handler = this.boundRemoveHandlers.get(button);
      if (handler) {
        button.removeEventListener('click', handler);
        this.boundRemoveHandlers.delete(button);
      }
    });

    // Remove promo toggle listener
    if (this.boundPromoToggleHandler && this.promoToggle) {
      this.promoToggle.removeEventListener('click', this.boundPromoToggleHandler);
      this.boundPromoToggleHandler = null;
    }

    // Remove close button listener
    if (this.closeButton && this.handleCloseClick) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }
  }

  bindCartContentEvents() {
    if (!this.content) return;

    // Handle cart item quantity changes (remove, add)
    const quantityButtons = this.content.querySelectorAll('.cart-drawer__quantity-remove, .cart-drawer__quantity-add');
    quantityButtons.forEach(button => {
      const handler = this.handleQuantityChange.bind(this);
      this.boundQuantityHandlers.set(button, handler);
      button.addEventListener('click', handler);
    });

    const removeButtons = this.content.querySelectorAll('.cart-drawer__item-remove');
    removeButtons.forEach(button => {
      const handler = this.handleQuantityChange.bind(this);
      this.boundRemoveHandlers.set(button, handler);
      button.addEventListener('click', handler);
    });

    // Handle promo code toggle
    this.promoToggle = this.content.querySelector('.cart-drawer__promo-toggle');
    if (this.promoToggle) {
      this.boundPromoToggleHandler = this.handlePromoToggle.bind(this);
      this.promoToggle.addEventListener('click', this.boundPromoToggleHandler);
    }
  }

  async handleQuantityChange(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const item = button.closest('.cart-drawer__item');
    const quantityElement = item.querySelector('.cart-drawer__quantity-value');
    const currentQuantity = parseInt(quantityElement.textContent);

    // Get line item key from data attribute (preferred for precise updates)
    const lineItemKey = item.getAttribute('data-item-key');
    const lineNumber = item.getAttribute('data-item-line');

    if (!lineItemKey && !lineNumber) {
      console.error('No line item identifier found for cart item');
      return;
    }

    let newQuantity;

    if (button.classList.contains('cart-drawer__quantity-add')) {
      newQuantity = currentQuantity + 1;
    } else if (button.classList.contains('cart-drawer__item-remove')) {
      newQuantity = 0;
    } else if (button.classList.contains('cart-drawer__quantity-remove')) {
      newQuantity = currentQuantity > 1 ? currentQuantity - 1 : 0;
    }

    await this.updateCartLineItem(lineItemKey, lineNumber, newQuantity, currentQuantity);
  }

  async updateCartLineItem(lineItemKey, lineNumber, quantity, previousQuantity) {
    try {
      if (this.content) {
        this.content.classList.add('cart-drawer__content--loading');
      }

      // Use /cart/change.js with line item key for precise single-item updates
      const requestBody = {
        id: lineItemKey,
        quantity: quantity
      };

      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.description || result.message || 'Failed to update cart';
        throw new Error(errorMessage);
      }

      // Check if the requested quantity was actually applied
      // Shopify may return a lower quantity if inventory is insufficient
      if (quantity > 0) {
        const updatedItem = result.items?.find(item => String(item.key) === String(lineItemKey));
        if (updatedItem && updatedItem.quantity < quantity) {
          const inventoryMessage = `Only ${updatedItem.quantity} available. Your cart has been updated to the maximum quantity.`;
          this.showError(inventoryMessage);
        }
      }

      document.dispatchEvent(new CustomEvent('cart:quantity-updated', {
        detail: { lineItemKey, quantity, cart: result }
      }));

      await this.refreshCart();

    } catch (error) {
      console.error('Error updating cart quantity:', error);
      this.showError(error.message);

      document.dispatchEvent(new CustomEvent('cart:quantity-error', {
        detail: { error, lineItemKey, quantity }
      }));

      await this.refreshCart();
    } finally {
      if (this.content) {
        this.content.classList.remove('cart-drawer__content--loading');
      }
    }
  }

  handlePromoToggle(event) {
    event.preventDefault();
    const promoForm = this.content.querySelector('.cart-drawer__promo-form');
    if (promoForm) {
      promoForm.style.display = promoForm.style.display === 'none' ? 'flex' : 'none';
    }
  }

  updateCartItemCount() {
    // Update all elements with data-cart-item-count attribute
    const countElements = document.querySelectorAll('[data-cart-item-count]');

    if (countElements.length === 0) return;

    // Get cart item count from the cart drawer content
    let itemCount = 0;

    if (this.content) {
      const cartItems = this.content.querySelectorAll('.cart-drawer__item');
      cartItems.forEach(item => {
        const quantityElement = item.querySelector('.cart-drawer__quantity-value');
        if (quantityElement) {
          itemCount += parseInt(quantityElement.textContent) || 0;
        }
      });
    }

    // Update all cart item count elements
    countElements.forEach(element => {
      element.textContent = itemCount;
    });
  }

  initAdditionalProducts() {
    // Initialize additional products functionality if additional products exist
    const additionalSection = this.content?.querySelector('.cart-drawer__additional');

    if (additionalSection) {
      // Clean up existing instance if it exists
      if (this.additionalProducts) {
        this.additionalProducts = null;
      }

      // Create new instance
      this.additionalProducts = new CartDrawerAdditionalProducts(this.content);
    }
  }

  #trapFocus() {
    if (!this.drawer) return;

    const focusableElements = this.drawer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus the first element
    firstElement.focus();

    // Handle tab key navigation
    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        // Shift + Tab (backwards)
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab (forwards)
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Clean up previous listener if exists
    if (this.focusHandler) {
      this.drawer.removeEventListener('keydown', this.focusHandler);
    }

    this.focusHandler = handleTabKey;
    this.drawer.addEventListener('keydown', this.focusHandler);
  }

  // Public methods for external use
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  getIsOpen() {
    return this.isOpen;
  }

  refresh() {
    return this.refreshCart();
  }

  /**
   * Add items to cart and open the drawer
   * @param {Array} items - Array of items with id and quantity properties
   * @returns {Promise} - Promise that resolves when items are added and drawer is opened
   */
  async addItemsAndOpen(items) {
    if (!items || !Array.isArray(items) || items.length === 0) {
      console.error('Invalid items array provided to addItemsAndOpen');
      return Promise.reject(new Error('Invalid items array'));
    }

    // Validate and normalize items format (ensure IDs are strings for large ID safety)
    const normalizedItems = [];
    for (const item of items) {
      if (!item.id || !item.quantity) {
        console.error('Each item must have id and quantity properties');
        return Promise.reject(new Error('Invalid item format'));
      }
      normalizedItems.push({
        id: String(item.id),
        quantity: item.quantity,
        ...(item.properties && { properties: item.properties })
      });
    }

    const requestData = { items: normalizedItems };

    try {
      // Add items to cart using Shopify Ajax Cart API
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.description || result.message || 'Failed to add to cart';
        throw new Error(errorMessage);
      }

      // Dispatch success event
      document.dispatchEvent(new CustomEvent('cart:items-added', {
        detail: { items: result.items || result, requestedItems: items }
      }));

      // Refresh cart content first, then open drawer (prevents stale content flash)
      await this.refreshCart();
      this.open();

      return result;

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showError(error.message);

      // Dispatch error event
      document.dispatchEvent(new CustomEvent('cart:add-items-error', {
        detail: { error, requestedItems: items }
      }));

      throw error;
    }
  }

  /**
   * Display an error message to the user
   * @param {string} message - Error message to display
   */
  showError(message = 'An error occurred. Please try again.') {
    // Create or update error notification element
    let errorNotification = document.querySelector('.cart-drawer__error-notification');

    if (!errorNotification) {
      errorNotification = document.createElement('div');
      errorNotification.className = 'cart-drawer__error-notification';
      errorNotification.setAttribute('role', 'alert');
      errorNotification.setAttribute('aria-live', 'polite');

      // Add styles inline for the notification
      errorNotification.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #dc3545;
        color: white;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 14px;
        z-index: 10001;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        opacity: 0;
        transition: opacity 0.3s ease;
        max-width: 90%;
        text-align: center;
      `;

      document.body.appendChild(errorNotification);
    }

    errorNotification.textContent = message;
    errorNotification.style.opacity = '1';

    // Auto-hide after 4 seconds
    clearTimeout(this.errorNotificationTimeout);
    this.errorNotificationTimeout = setTimeout(() => {
      errorNotification.style.opacity = '0';
    }, 4000);
  }
}

// Define custom element
// Additional Products functionality for cart drawer
class CartDrawerAdditionalProducts {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
    this.initQuantitySelectors();
    this.initFormSubmission();
  }

  initQuantitySelectors() {
    const quantitySelectors = this.cartDrawer.querySelectorAll('.cart-drawer__quantity-selector');

    for (const selector of quantitySelectors) {
      const input = selector.querySelector('.cart-drawer__quantity-selector-input');
      const decreaseBtn = selector.querySelector('.cart-drawer__quantity-selector-decrease');
      const increaseBtn = selector.querySelector('.cart-drawer__quantity-selector-increase');

      if (!input || !decreaseBtn || !increaseBtn) continue;

      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        if (currentValue > 1) {
          input.value = currentValue - 1;
          this.#updateQuantity(input);
        }
      });

      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(input.value) || 1;
        input.value = currentValue + 1;
        this.#updateQuantity(input);
      });

      input.addEventListener('change', () => {
        const value = parseInt(input.value) || 1;
        if (value < 1) {
          input.value = 1;
        }
        this.#updateQuantity(input);
      });

      input.addEventListener('input', () => {
        const value = input.value.replace(/[^0-9]/g, '');
        input.value = value;
      });
    }
  }

  initFormSubmission() {
    const forms = this.cartDrawer.querySelectorAll('.cart-drawer__product-form');

    for (const form of forms) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation(); // Prevent document-level handler from also processing
        this.#handleFormSubmission(form);
      });
    }
  }

  async #handleFormSubmission(form) {
    const submitButton = form.querySelector('.cart-drawer__product-add-to-cart');

    if (!submitButton || submitButton.disabled) return;

    // Show loading state
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Adding...';
    submitButton.disabled = true;

    try {
      const formData = new FormData(form);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.description || result.message || 'Failed to add to cart';
        throw new Error(errorMessage);
      }

      // Refresh cart after successful addition
      const cartDrawerElement = document.querySelector('cart-drawer');
      if (cartDrawerElement && cartDrawerElement.refreshCart) {
        await cartDrawerElement.refreshCart();
      }

      // Dispatch custom event for cart update
      document.dispatchEvent(new CustomEvent('cart:updated'));

    } catch (error) {
      console.error('Error adding product to cart:', error);

      // Use the cart drawer's error notification instead of alert
      if (cartDrawerElement && cartDrawerElement.showError) {
        cartDrawerElement.showError(error.message);
      }

      // Show error state briefly
      submitButton.textContent = 'Error';
      setTimeout(() => {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
      }, 2000);
      return;
    }

    // Reset button state
    submitButton.textContent = originalText;
    submitButton.disabled = false;
  }

  #updateQuantity(input) {
    // Dispatch a custom event that can be listened to by other components
    const event = new CustomEvent('quantity:change', {
      detail: {
        quantity: parseInt(input.value),
        form: input.closest('form')
      },
      bubbles: true
    });

    input.dispatchEvent(event);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Initialize cart drawer when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Create and append cart drawer element if it doesn't exist
  if (!document.querySelector('cart-drawer')) {
    const cartDrawerElement = document.createElement('cart-drawer');
    document.body.appendChild(cartDrawerElement);
  }
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartDrawer;
}
